import { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { NavLink, useLocation } from 'react-router'

import { DocumentationLink } from 'components/ui'
import { serverEditionsRoutePath } from 'config/routePaths'
import AppLogo from 'Layout/AppLogo'
import useCurrentAccount from 'query/useCurrentAccount'
import useCurrentUser from 'query/useCurrentUser'
import { isAdmin, isPaidAccount } from 'utils/validation'

import UserBox from './Components/UserBox'

const productNavigation = [
  { label: 'Configurations', to: '/configurations' },
  { label: 'Users', to: '/users' },
  { label: 'Devices', to: '/devices' },
  { label: 'Server Editions', to: serverEditionsRoutePath },
]

const HeaderLink = ({ label, to }) => (
  <NavLink
    className={({ isActive }) => cx(
      'ds-nav-link tw:relative tw:z-10 tw:flex tw:h-[72px] tw:items-center tw:px-3 tw:text-lg tw:text-foreground-muted tw:no-underline tw:transition-colors tw:duration-fast tw:ease-standard tw:hover:text-foreground-strong tw:hover:no-underline',
      isActive && 'tw:text-foreground-strong',
    )}
    to={to}
  >
    {label}
  </NavLink>
)

const AppHeader = () => {
  const location = useLocation()
  const mobileToggleRef = useRef(null)
  const previousPathnameRef = useRef(location.pathname)
  const primaryNavigationRef = useRef(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [navigationIndicator, setNavigationIndicator] = useState(null)
  const [navigationIndicatorReady, setNavigationIndicatorReady] = useState(false)
  const { data: currentUser = {} } = useCurrentUser()
  const { data: currentAccount = {} } = useCurrentAccount({ enabled: isAdmin(currentUser) })
  const showProductNavigation = isAdmin(currentUser) && isPaidAccount(currentAccount)

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return
    previousPathnameRef.current = location.pathname

    if (mobileOpen) {
      setMobileOpen(false)
      requestAnimationFrame(() => mobileToggleRef.current?.focus())
    }
  }, [location.pathname, mobileOpen])

  useEffect(() => {
    const navigation = primaryNavigationRef.current

    if (!navigation || !showProductNavigation) {
      setNavigationIndicator(null)
      return undefined
    }

    let cancelled = false

    const measureIndicator = () => {
      const activeLink = navigation.querySelector('[aria-current="page"]')

      if (!activeLink) {
        setNavigationIndicator(null)
        return
      }

      const horizontalInset = 12

      setNavigationIndicator({
        left: activeLink.offsetLeft + horizontalInset,
        width: Math.max(activeLink.offsetWidth - (horizontalInset * 2), 0),
      })
    }

    measureIndicator()
    const readyFrame = requestAnimationFrame(() => setNavigationIndicatorReady(true))
    window.addEventListener('resize', measureIndicator)

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(measureIndicator)

    resizeObserver?.observe(navigation)

    document.fonts?.ready.then(() => {
      if (!cancelled) measureIndicator()
    })

    return () => {
      cancelled = true
      cancelAnimationFrame(readyFrame)
      window.removeEventListener('resize', measureIndicator)
      resizeObserver?.disconnect()
    }
  }, [location.pathname, showProductNavigation])

  useEffect(() => {
    if (!mobileOpen) {
      return undefined
    }

    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setMobileOpen(false)
        requestAnimationFrame(() => mobileToggleRef.current?.focus())
      }
    }

    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [mobileOpen])

  return (
    <header className="tw:sticky tw:top-0 tw:z-40 tw:border-b tw:border-default tw:bg-surface/95 tw:backdrop-blur-sm">
      <div className="tw:mx-auto tw:flex tw:h-[72px] tw:w-full tw:max-w-content tw:items-center tw:px-5 tw:mobile:px-4">
        <AppLogo />

        {showProductNavigation && (
          <nav
            aria-label="Primary"
            className="tw:relative tw:ml-8 tw:flex tw:h-full tw:items-center tw:gap-1 tw:shell:hidden"
            ref={primaryNavigationRef}
          >
            <span
              aria-hidden="true"
              className="ds-nav-indicator"
              data-ready={navigationIndicatorReady ? 'true' : undefined}
              style={navigationIndicator
                ? {
                  '--ds-nav-indicator-left': `${navigationIndicator.left}px`,
                  '--ds-nav-indicator-width': `${navigationIndicator.width}px`,
                  opacity: 1,
                }
                : { opacity: 0 }}
            />
            {productNavigation.map(item => <HeaderLink {...item} key={item.to} />)}
          </nav>
        )}

        <div className="tw:ml-auto tw:flex tw:items-center tw:gap-4">
          <DocumentationLink
            className={showProductNavigation ? 'tw:shell:hidden' : undefined}
            direction="left"
            href="https://docs.expandrive.com"
            label="Docs"
          />
          <UserBox />
          {showProductNavigation && (
            <button
              aria-expanded={mobileOpen}
              aria-label="Toggle Navigation"
              className="tw:hidden tw:h-11 tw:w-11 tw:items-center tw:justify-center tw:rounded-button tw:border tw:border-default tw:bg-surface tw:text-foreground-strong tw:shell:flex"
              onClick={() => setMobileOpen(open => !open)}
              ref={mobileToggleRef}
              type="button"
            >
              <span className="tw:sr-only">Menu</span>
              <span className="tw:grid tw:gap-1.5">
                <span className={cx('tw:block tw:h-px tw:w-5 tw:bg-current tw:transition-transform tw:duration-base tw:ease-out-quart', mobileOpen && 'tw:translate-y-[3.5px] tw:rotate-45')} />
                <span className={cx('tw:block tw:h-px tw:w-5 tw:bg-current tw:transition-transform tw:duration-base tw:ease-out-quart', mobileOpen && 'tw:-translate-y-[3.5px] tw:-rotate-45')} />
              </span>
            </button>
          )}
        </div>
      </div>

      {mobileOpen && (
        <nav aria-label="Mobile Primary" className="ds-mobile-sheet tw:absolute tw:left-0 tw:top-[72px] tw:w-full tw:rounded-b-modal tw:border-b tw:border-default tw:bg-surface-raised tw:p-4 tw:shadow-md">
          {productNavigation.map(item => (
            <NavLink
              className={({ isActive }) => cx(
                'tw:flex tw:min-h-12 tw:items-center tw:rounded-sm tw:px-4 tw:text-base tw:text-foreground-muted tw:no-underline tw:hover:bg-surface-hover tw:hover:text-foreground-strong tw:hover:no-underline',
                isActive && 'tw:bg-surface-subtle tw:text-foreground-strong',
              )}
              key={item.to}
              to={item.to}
            >
              {item.label}
            </NavLink>
          ))}
          <div className="tw:mt-2 tw:border-t tw:border-default tw:pt-2">
            <a
              className="tw:flex tw:min-h-12 tw:items-center tw:rounded-sm tw:px-4 tw:text-base tw:text-foreground-muted tw:no-underline tw:hover:bg-surface-hover tw:hover:text-foreground-strong tw:hover:no-underline"
              href="https://docs.expandrive.com"
              rel="noreferrer"
              target="_blank"
            >
              Documentation
            </a>
          </div>
        </nav>
      )}
    </header>
  )
}

export default AppHeader
